import { Trans, useLingui } from '@lingui/react/macro';
import { Zap } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { countIndividualShifts, summarisePreview } from '@/domain/templates/preview-stats';
import { formatHoursDisplay } from '@/domain/templates/preview-utils';
import type { SavedTemplate } from '@/types/saved-template';

import { LivePreview } from './live-preview';

interface TemplatePreviewModalProps {
  /** The template to preview. */
  template: SavedTemplate;
  /** Close the preview. */
  onClose: () => void;
  /** Start generating a rota from this template. Omit to hide Generate rota, e.g. for a deleted template. */
  onGenerate?: () => void;
}

/**
 * Read-only preview of a template (the v2 list "Preview" action): a header
 * (name + description), a stats panel (patterns / shifts / hours / scope), the
 * per-person pill grid via {@link LivePreview}, and a Generate-rota footer.
 * Mirrors the Build workspace's live preview so the two surfaces match.
 */
export function TemplatePreviewModal({ template, onClose, onGenerate }: TemplatePreviewModalProps) {
  const { t } = useLingui();
  const shiftCount = countIndividualShifts(template.shifts);
  const hours = formatHoursDisplay(summarisePreview(template.shifts).hours);
  const scope = (template.scope.sites?.length ?? 0) === 0 ? t`All` : t`Specific`;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        showCloseButton={false}
        className="flex max-h-[90vh] w-[95vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-6xl"
      >
        <DialogHeader className="border-b px-6 py-4 text-left">
          <p className="text-xs tracking-wide text-muted-foreground uppercase">
            <Trans>Template preview</Trans>
          </p>
          <DialogTitle className="text-[length:var(--section-title-font-size)] leading-[var(--section-title-line-height)]">
            {template.name}
          </DialogTitle>
          {template.description ? (
            <DialogDescription>{template.description}</DialogDescription>
          ) : (
            <DialogDescription className="sr-only">
              <Trans>The template&apos;s recurring weekly schedule.</Trans>
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-border sm:grid-cols-4">
            <Stat value={String(template.shifts.length)} label={<Trans>Patterns</Trans>} />
            <Stat value={String(shiftCount)} label={<Trans>Shifts</Trans>} />
            <Stat value={`${hours}h`} label={<Trans>Hours</Trans>} />
            <Stat value={scope} label={<Trans>Sites</Trans>} />
          </div>

          {template.shifts.length === 0 ? (
            <p className="rounded-md border border-dashed p-8 text-center text-[length:var(--body-font-size)] leading-[var(--body-line-height)] text-[var(--muted-foreground)]">
              <Trans>This template has no shifts yet.</Trans>
            </p>
          ) : (
            <LivePreview
              shifts={template.shifts}
              showStats={false}
              demandScaling={template.demandScaling}
            />
          )}
        </div>

        <div className="flex justify-end gap-2 border-t px-6 py-4">
          <Button variant="outline" onClick={onClose}>
            <Trans>Close</Trans>
          </Button>
          {onGenerate && (
            <Button onClick={onGenerate} aria-label={t`Generate rota`}>
              <Zap className="size-4" />
              <Trans>Generate rota</Trans>
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** A single value/label cell in the preview's stats panel. */
function Stat({ value, label }: { value: string; label: ReactNode }) {
  return (
    <div className="bg-card p-3 text-center">
      <div className="text-[length:var(--section-title-font-size)] leading-[var(--section-title-line-height)] font-semibold">
        {value}
      </div>
      <div className="mt-0.5 text-[length:var(--extra-small-font-size)] leading-[var(--extra-small-line-height)] tracking-wide text-muted-foreground uppercase">
        {label}
      </div>
    </div>
  );
}
